import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    FIELD_LABELS,
    formatDate,
    formatFloat,
    shortLabel,
    useNumber,
} from '../../../../../../helpers';
import {
  OrderAdjustmentsPageQuery,
  useOrderAdjustmentsPageLazyQuery
} from '../../../../../../services';
import {
  CCardList,
  CMinifiedTable,
  DateRange,
  DBlock,
  DDateRangePicker,
  DIdAutocomplete,
  ProductScope,
  selectPaginated,
  usePaginatedListBlock
} from '../../../../../dum';
import {
  SDeleteOrderAdjustmentDialog,
  SUpsertOrderAdjustmentDialog
} from '../../../../../smart';
import { OrderAdjustmentCard } from './order-adjustment-card';

type AdjustmentDoc =
    OrderAdjustmentsPageQuery['paginatedOrderAdjustments']['docs'][number];

interface IOrderAdjustmentsListBlock {
    // When set, embeds the block on a product's page (all adjustments of that
    // product). Undefined renders the full standalone Ajustes page.
    scope?: ProductScope;
    // Isolates filter/paginator localStorage state. Pass a unique key when
    // embedding (e.g. on the product page) so state doesn't collide with the
    // standalone Ajustes page.
    storageKey?: string;
    // Pins the list to a single adjustment type and hides the type filter
    // control (Devoluciones page). The user can't widen past this type.
    lockedOrderAdjustmentTypeId?: number;
    // Overrides the standalone list heading (e.g. 'Devoluciones').
    title?: string;
}

export function OrderAdjustmentsListBlock({
    scope,
    storageKey,
    lockedOrderAdjustmentTypeId,
    title = 'Ordenes de ajuste',
}: IOrderAdjustmentsListBlock = {}) {
    const KEY = storageKey ?? OrderAdjustmentsListBlock.name;
    const embedded = !!scope;
    const productId = scope?.id ?? null;
    const typeLocked = lockedOrderAdjustmentTypeId != null;

    const [query, { data, previousData, loading }] =
        useOrderAdjustmentsPageLazyQuery();

    const [orderAdjustmentTypeId, setOrderAdjustmentTypeId] = useNumber({
        KEY: KEY + '_OrderAdjustmentTypeId',
    });

    // A locked type wins over the user filter and is what the query and the
    // reset-on-change logic below actually use.
    const effectiveOrderAdjustmentTypeId = typeLocked
        ? lockedOrderAdjustmentTypeId
        : orderAdjustmentTypeId;

    const {
        filter,
        setFilter,
        debouncedFilter,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
        selectedId: orderAdjustmentId,
        upsertOpen,
        setUpsertOpen,
        deleteOpen,
        setDeleteOpen,
        openEdit,
        openDelete,
    } = usePaginatedListBlock({
        storageKey: KEY,
        embedded,
        resetDeps: [effectiveOrderAdjustmentTypeId],
    });

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                startDate: startDate || null,
                endDate: endDate || null,
                orderAdjustmentTypeId: effectiveOrderAdjustmentTypeId,
                productId: productId,
                filter: debouncedFilter || null,
            },
        });
    }, [query, skip, take, startDate, endDate, effectiveOrderAdjustmentTypeId, productId, debouncedFilter]);

    const handleEditOrderAdjustment = useCallback(
        (oa: AdjustmentDoc) => openEdit(oa.id),
        [openEdit],
    );
    const handleDeleteOrderAdjustment = useCallback(
        (oa: AdjustmentDoc) => openDelete(oa.id),
        [openDelete],
    );

    const { docs: orderAdjustments, count } = selectPaginated(
        data?.paginatedOrderAdjustments,
        previousData?.paginatedOrderAdjustments,
    );

    // Sum each row's product lines once (memoized) for the embedded columns.
    const summaries = useMemo(
        () =>
            embedded
                ? orderAdjustments.map((oa) => {
                      const lines = oa.order_adjustment_products.filter(
                          (p) => p.product_id === productId,
                      );
                      return {
                          kilos: lines.reduce((a, p) => a + p.kilos, 0),
                          groups: lines.reduce((a, p) => a + p.groups, 0),
                      };
                  })
                : [],
        [orderAdjustments, productId, embedded],
    );

    if (scope) {
        return (
            <>
                <CMinifiedTable
                    title={'Ajustes'}
                    loading={loading}
                    filterValue={filter}
                    onFilterChange={(value) => setFilter(value || '')}
                    filterHelperText={'Tipo de ajuste, producto, folio de venta o factura.'}
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    count={count}
                    onRowDoubleClick={(oa) => handleEditOrderAdjustment(oa)}
                    items={[
                        { title: shortLabel(FIELD_LABELS.date), textAlign: 'center', content: (oa) => formatDate(oa.date) },
                        { title: shortLabel(FIELD_LABELS.order_adjustment_type_id), content: (oa) => oa.order_adjustment_type?.name },
                        { title: shortLabel(FIELD_LABELS.order_sale_id), content: (oa) => (oa.order_sale ? '#' + oa.order_sale.order_code : '—') },
                        { title: shortLabel(FIELD_LABELS.groups), textAlign: 'right', content: (_oa, i) => formatFloat(summaries[i]?.groups ?? 0) },
                        { title: shortLabel(FIELD_LABELS.kilos), textAlign: 'right', content: (_oa, i) => formatFloat(summaries[i]?.kilos ?? 0) },
                    ]}
                    data={orderAdjustments}
                />
                <SUpsertOrderAdjustmentDialog
                    open={upsertOpen}
                    setOpen={setUpsertOpen}
                    orderAdjustmentId={orderAdjustmentId}
                    lockedOrderAdjustmentTypeId={lockedOrderAdjustmentTypeId}
                />
                <SDeleteOrderAdjustmentDialog
                    open={deleteOpen}
                    setOpen={setDeleteOpen}
                    orderAdjustmentId={orderAdjustmentId}
                />
            </>
        );
    }

    return (
        <DBlock>
            <CCardList<AdjustmentDoc>
                title={title}
                data={orderAdjustments}
                loading={loading}
                keyOf={(oa) => oa.id}
                renderItem={(oa) => (
                    <OrderAdjustmentCard
                        orderAdjustment={oa}
                        onEdit={handleEditOrderAdjustment}
                        onDelete={handleDeleteOrderAdjustment}
                    />
                )}
                skip={skip}
                take={take}
                count={count}
                setSkip={setSkip}
                filterValue={filter}
                onFilterChange={(value) => setFilter(value || '')}
                filterHelperText={'Tipo de ajuste, producto, folio de venta o factura.'}
                activeFilterCount={
                    (startDate && endDate ? 1 : 0) +
                    (!typeLocked && orderAdjustmentTypeId != null ? 1 : 0)
                }
                filters={
                    <>
                        <DDateRangePicker
                            label={'Fecha'}
                            value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : undefined}
                            onChange={(range: DateRange) => {
                                setStartDate(range[0].format(DATE_FORMAT));
                                setEndDate(range[1].format(DATE_FORMAT));
                            }}
                            onClear={() => { setStartDate(''); setEndDate(''); }}
                            disableGutters
                        />
                        {/* The type is fixed on the Devoluciones page, so its
                            filter control is hidden there. */}
                        {!typeLocked ? (
                            <DIdAutocomplete
                                id={orderAdjustmentTypeId}
                                onChange={setOrderAdjustmentTypeId}
                                itemText={'name'}
                                label={'Tipo'}
                                options={data?.getOrderAdjustmentTypes || []}
                                disableGutters
                            />
                        ) : null}
                    </>
                }
                onItemDoubleClick={handleEditOrderAdjustment}
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        callback: (orderAdjustment) => {
                            handleEditOrderAdjustment(orderAdjustment);
                        },
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        disable: (orderAdjustment) => {
                            return !orderAdjustment.is_deletable;
                        },
                        callback: (orderAdjustment) => {
                            handleDeleteOrderAdjustment(orderAdjustment);
                        },
                    },
                ]}
            />
            <SUpsertOrderAdjustmentDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                orderAdjustmentId={orderAdjustmentId}
                lockedOrderAdjustmentTypeId={lockedOrderAdjustmentTypeId}
            />
            <SDeleteOrderAdjustmentDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                orderAdjustmentId={orderAdjustmentId}
            />
        </DBlock>
    );
}
